/**
 * Scoped device capabilities (C5).
 * See docs/architecture/2026-07-24-device-identity-and-capabilities.md.
 *
 * Authorization was all-or-nothing: authMiddleware asked one question — is this
 * token the API key — and answered 401 or full access. A device paired merely to
 * glance at session status held exactly the authority of the one driving the
 * agent, because there was only ever one credential and no principal to scope.
 */

export const CAPABILITIES = [
  "history:read", // read conversations, search
  "session:control", // start, resume, send input, interrupt
  "fs:browse", // browse the project tree
  "fs:upload", // upload files into a project
  "notifications", // register for push
  "admin", // rotate keys, manage devices
] as const;

export type Capability = (typeof CAPABILITIES)[number];

export function isCapability(value: unknown): value is Capability {
  return typeof value === "string" && (CAPABILITIES as readonly string[]).includes(value);
}

/**
 * Every capability, `admin` included. What a normal driving device gets.
 *
 * It withheld `admin` originally, on the reasoning that a device should run the
 * agent without being able to rotate credentials or revoke its siblings. That is
 * a sound rule for a server shared between people, and this one is not: the
 * phone IS the administration surface. Mobile's paired-devices screen
 * (`GET /api/devices`), backup and restore (`/api/backup/*`) and the model and
 * effort settings (`/api/config/claude-flags`) are all admin-gated, so a device
 * without `admin` loses four working screens the moment it starts presenting its
 * own token instead of the shared key.
 *
 * `read-only` is where the narrowing lives, and it still does.
 */
export const FULL_CAPABILITIES: Capability[] = [
  "history:read",
  "session:control",
  "fs:browse",
  "fs:upload",
  "notifications",
  "admin",
];

/**
 * Read-only monitoring. The mode that makes a "just show me what's happening"
 * device safe to pair, and the one C5 explicitly calls for.
 */
export const READ_ONLY_CAPABILITIES: Capability[] = ["history:read"];

export type CapabilityPreset = "full" | "read-only";

export function capabilitiesForPreset(preset: CapabilityPreset): Capability[] {
  return preset === "read-only" ? [...READ_ONLY_CAPABILITIES] : [...FULL_CAPABILITIES];
}

/**
 * The principal behind a request.
 *
 * `legacy` is the shared API key: it predates device identity, so it carries the
 * full preset and no device id. Keeping it working is what lets this ship
 * without breaking every already-paired device.
 */
export interface Principal {
  kind: "device" | "legacy";
  deviceId?: string;
  capabilities: Capability[];
}

export function legacyPrincipal(): Principal {
  // The shared API key is the OWNER's credential — it is what pairs new devices
  // and rotates the key itself. It therefore holds `admin` in addition to the
  // full preset. Devices are the things that get scoped; the key that mints
  // them cannot be, or the owner could no longer administer their own server.
  return { kind: "legacy", capabilities: [...FULL_CAPABILITIES] };
}

export function hasCapability(principal: Principal, required: Capability): boolean {
  return principal.capabilities.includes(required);
}

/**
 * Capability required to reach a route.
 *
 * Matching is longest-prefix so a specific rule beats a general one. Returning
 * `null` means "no rule".
 *
 * **The caller ALLOWS in that case — it does not deny.** `authMiddleware` calls
 * `next()` for an unclassified path so an unknown route still 404s, because a
 * 403 would tell an authenticated caller that a path it cannot name might
 * exist. This comment previously claimed the opposite, which mattered: a reader
 * trusting it would believe a route was guarded by omission when it is not.
 *
 * The fail-closed guarantee is therefore a BUILD-TIME one, enforced by
 * `__tests__/capabilities.test.ts` ("every mounted route is classified"), which
 * reads both the mount prefixes in `api/app.ts` and the path literals in
 * `api/routes/*.ts`. A new endpoint with no mapping fails that test rather than
 * being denied at runtime. If you change this function's contract, change that
 * test and this comment together — they are the whole guarantee.
 */
const ROUTE_CAPABILITIES: ReadonlyArray<[prefix: string, capability: Capability]> = [
  // Most specific first for readability; matching sorts by length anyway.
  ["/api/sessions/", "session:control"],
  ["/api/sessions", "history:read"], // listing sessions is a read
  ["/api/conversations", "history:read"],
  ["/api/projects", "history:read"],
  ["/api/search", "history:read"],
  ["/api/providers", "history:read"],
  ["/api/browse", "fs:browse"],
  ["/api/upload", "fs:upload"],
  ["/api/push", "notifications"],
  ["/api/devices", "admin"],
  ["/api/config", "admin"],
  ["/api/auth/rotate", "admin"],
  ["/api/backup", "admin"],
  // Server identity and capability discovery. A read-only device must be able
  // to see WHICH server it is talking to and what it supports, or it cannot
  // render anything at all.
  ["/api/info", "history:read"],
  ["/api/profiles", "history:read"],
  ["/api/diagnostics", "history:read"],
  ["/api/cache/alert", "history:read"],
  // Client log shipping: any authenticated client may report its own errors.
  // Gating this behind a capability would silence diagnostics from exactly the
  // devices most likely to be misbehaving.
  ["/api/__client-log", "history:read"],
  // Logs viewer is localhost-only and already bypasses this middleware; the
  // mapping exists so a remote request is classified rather than denied as
  // unclassified.
  ["/api/logs", "admin"],
  // Pairing routes other than the public exchange (e.g. minting a token).
  ["/api/pair", "admin"],
  // The transport handshake. `POST /api/e2ee/open` is public and bypasses this
  // middleware entirely (`PUBLIC_POST_PATHS`) — the Noise handshake IS the
  // authentication. The rule exists so a stray method on the same path is
  // denied by rule rather than by omission, which is what the "every mounted
  // route is classified" test enforces (NONCE-DESIGN §18).
  ["/api/e2ee", "admin"],
  // The live WebSocket. Subscribing is a read — terminal output, session
  // updates, conversation events. Control still flows through the HTTP input
  // routes, which carry their own capability check, so a read-only device can
  // watch a session stream without being able to drive it.
  ["/ws", "history:read"],
  // Progress webhook (multi-agent). Authenticated by HMAC in the handler and
  // already skipped by the middleware; classified so a stray request is denied
  // by rule rather than as "unclassified".
  ["/internal/sessions", "admin"],
];

export function requiredCapability(path: string, method: string): Capability | null {
  // A read of the session list is not the same authority as driving a session.
  // GET /api/sessions/:id and its sub-resources are reads; writes to them are
  // control. Without this split a read-only device could not see a session at
  // all, which would make the mode useless.
  if (path.startsWith("/api/sessions") && (method === "GET" || method === "HEAD")) {
    return "history:read";
  }

  let best: { len: number; cap: Capability } | null = null;
  for (const [prefix, cap] of ROUTE_CAPABILITIES) {
    if (path.startsWith(prefix) && (best === null || prefix.length > best.len)) {
      best = { len: prefix.length, cap };
    }
  }
  return best?.cap ?? null;
}
